// Core packages
import { Injectable } from '@angular/core';

// Third party packages
import { Observable } from 'rxjs';
import { catchError } from 'rxjs/operators';

// Custom packages
import { CookieService } from 'ngx-cookie-service';
import { ListApiResponseV2 } from 'src/app/shared/interfaces/_base/api-response.interface';
import { ApiService } from 'src/app/shared/services/api.service';
import { HelperService } from 'src/app/shared/services/helper.service';

@Injectable({
  providedIn: 'root'
})
export class PlatformNotesService {

  /**
   * Class constructor
   */
  constructor(
    private apiService: ApiService,
    private helperService: HelperService,
    private cookieService: CookieService
  ) { }
  
  /**
   * Create a new company note
   *
   * @since 1.0.0
   *
   * @param companyId ID Company
   * @param data Company note data
   */
  createCompanyNote(companyId: string, data: any): Observable<any> {
    return this.apiService.apiCall('POST', `platform-notes/company/${companyId}`, data);
  }
    
  /**
   * Create a new property note
   *
   * @since 1.0.0
   *
   * @param propertyId ID Property
   * @param data Property note data
   */
  createPropertyNote(propertyId: string, data: any): Observable<any> {
    return this.apiService.apiCall('POST', `platform-notes/property/${propertyId}`, data);
  }

  /**
   * Get platform notes list for given query params
   *
   * @since 1.0.0
   *
   * @param [historical] Type of list
   * @param [companyId] Company Id
   * @param [propertyId] Property Id
   * @param [start] Query left offset
   * @param [limit] Results number
   * @param [sort] Sorting field name
   * @param [sortVersus] Sorting versus. Can be 'asc' or 'desc'
   * @param [search] Filtering object with fildering data string
   */
  getList(historical: boolean, companyId: string, propertyId: string, start?: number, limit?: number, sort?: string, sortVersus?: string, search?: any): Observable<ListApiResponseV2> {
    const params: any = { start, limit, sort, sortVersus };
    if (historical) {
      search['historical'] = true;
    }
    if (search) {
      Object.keys(search).forEach(key => {
        const newParamKey = `search[${key}]`;
        const newParamValue = search[key];
        params[newParamKey] = newParamValue;
      });
    }
    
    if (propertyId !== '') {
      return this.apiService.apiCall('GET', `platform-notes/property/${propertyId}`, params);  
    }
    return this.apiService.apiCall('GET', `platform-notes/company/${companyId}`, params);
  }

  /**
   * Get all company notes with properties
   */
  getCompanyProperty(companyId: string, propertyId: string): Observable<any> {
    return this.apiService.apiCall('GET', `platform-notes/company/${companyId}/${propertyId}`, {});
  }

  /**
   * Get an exportation file of all platform note of company
   *
   * @since 1.0.0
   */
  getCompanyExport(companyId: string, historical?: boolean): Observable<any> {
    return this.apiService.apiCall('GET', `platform-notes/company-export/${companyId}`, {historical}, { responseType: 'blob' })
      .pipe(
        catchError(err => {
          return this.helperService.parseErrorBlob(err);
        })
      );
  }

  /**
   * Get an exportation file of all platform note of property
   *
   * @since 1.0.0
   */
  getPropertyExport(propertyId: string, historical?: boolean): Observable<any> {
    return this.apiService.apiCall('GET', `platform-notes/property-export/${propertyId}`, {historical}, { responseType: 'blob' })
      .pipe(
        catchError(err => {
          return this.helperService.parseErrorBlob(err);
        })
      );
  }
  
  /**
   * Get notes of company
   */
  getCompany(companyId: string, start?: number, limit?: number, sort?: string, sortVersus?: string, search?: any): Observable<any> {
    const params: any = { start, limit, sort, sortVersus };
    if (search) {
      Object.keys(search).forEach(key => {
        const newParamKey = `search[${key}]`;
        const newParamValue = search[key];
        params[newParamKey] = newParamValue;
      });
    }

    return this.apiService.apiCall('GET', `platform-notes/company/${companyId}`, params);
  }
  
  /**
   * Get notes of company
   */
  getCompanyWithProperties(companyId: string, start?: number, limit?: number, sort?: string, sortVersus?: string, search?: any): Observable<any> {
    const params: any = { start, limit, sort, sortVersus };
    if (search) {
      Object.keys(search).forEach(key => {
        const newParamKey = `search[${key}]`;
        const newParamValue = search[key];
        params[newParamKey] = newParamValue;
      });
    }

    return this.apiService.apiCall('GET', `platform-notes/company-with-properties/${companyId}`, params);
  }

  /**
   * Get notes of property
   */
  getProperty(propertyId: string, start?: number, limit?: number, sort?: string, sortVersus?: string, search?: any): Observable<any> {
    const params: any = { start, limit, sort, sortVersus };
    if (search) {
      Object.keys(search).forEach(key => {
        const newParamKey = `search[${key}]`;
        const newParamValue = search[key];
        params[newParamKey] = newParamValue;
      });
    }

    return this.apiService.apiCall('GET', `platform-notes/property/${propertyId}`, params);
  }

  /**
   * Get notes of all property balances
   */
  getPropertyBalancesNotes(propertyId: string): Observable<any> {
    return this.apiService.apiCall('GET', `platform-notes/property-balances/${propertyId}`, {});
  }

  /**
   * Get notes of all company balances
   */
  getCompanyBalancesNotes(companyId: string): Observable<any> {
    return this.apiService.apiCall('GET', `platform-notes/company-balances/${companyId}`, {});
  }

  /**
   * Get creators list
   */
  getCreators(start?: number, limit?: number, sort?: string, sortVersus?: string, search?: any): Observable<any> {
    const params: any = { start, limit, sort, sortVersus };
    if (search) {
      Object.keys(search).forEach(key => {
        const newParamKey = `search[${key}]`;
        const newParamValue = search[key];
        params[newParamKey] = newParamValue;
      });
    }

    return this.apiService.apiCall('GET', `platform-notes/creators`, params);
  }

  /**
   * Update fileds of note
   *
   * @since 1.0.0
   *
   * @param noteId ID of note
   * @param data fileds of note
   */
  updateNote(noteId: string, data: any): Observable<any> {
    return this.apiService.apiCall('PATCH', `platform-notes/${noteId}`, data);
  }
      
  /**
   * Deactivate note
   *
   * @since 1.0.0
   *
   * @param noteId ID of note
   */
  deactivateNote(noteId: string): Observable<any> {
    return this.apiService.apiCall('PATCH', `platform-notes/${noteId}/deactivate`, {});
  }
      
  /**
   * Move the property notes to another company
   *
   * @since 1.0.0
   *
   * @param noteId ID of note
   */
  movePropertyCompany(propertyId: string, companyId: string, notesId: string[]): Observable<any> {
    const data = {
      propertyId,
      companyId,
      notesId
    }
    return this.apiService.apiCall('PATCH', `platform-notes/move-property-company`, data);
  }
      
  /**
   * Delete the property notes from company
   *
   * @since 1.0.0
   *
   * @param noteId ID of note
   */
  deletePropertyCompany(propertyId: string, companyId: string): Observable<any> {
    const data = {
      propertyId,
      companyId
    }
    return this.apiService.apiCall('PATCH', `platform-notes/delete-property-company`, data);
  }

  /**
   * Update notes of property reactivated
   *
   * @since 1.0.0
   *
   * @param noteId ID of note
   */
  propertyReactivated(propertyId: string, notesId: string[]): Observable<any> {
    const data = {
      notesId
    }
    return this.apiService.apiCall('PATCH', `platform-notes/property-reactivated/${propertyId}`, data);
  }


  /**
   * Delete note
   *
   * @since 1.0.0
   *
   * @param noteId ID of note
   */
  deleteNote(noteId: string): Observable<any> {
    return this.apiService.apiCall('DELETE', `platform-notes/${noteId}`);
  }
}
